import React, { useEffect, useState } from 'react';
import {
  Button,
  Container,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  TextField,
  IconButton,
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  FormLabel,
  Snackbar,
  Alert,
  Autocomplete, // Added Autocomplete import
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import axios from 'axios';
import { toast } from 'react-toastify';
import BeatLoader from 'react-spinners/BeatLoader';

export default function ItemDialog({ dialogOpen, setDialogOpen, updateID }) {
  const [itemData, setItemData] = useState({
    supplierName: '',
    grndate: '',
    totalAmount: '',
    paidAmount: '',
    pendingAmount: '',
    itemName: '',
    qty: '',
    baseunitprice: '',
    paymentMethod: 'Cash',
    expirationDate: '',
  });
  const [supplierList, setSupplierList] = useState([]);
  const [itemList, setItemList] = useState([]);

  const [selectedItem, setSelectedItem] = useState(null); // Changed initial state to null
  const [loading, setLoading] = useState(false);

  const normalizeSearch = (value) =>
    (value ?? '').toString().trim().toLowerCase();

  // Auto-calc totals from cost price * qty and pending from paid
  useEffect(() => {
    const qtyVal = parseFloat(itemData.qty) || 0;
    const costVal = selectedItem ? parseFloat(selectedItem.costPrice) || 0 : 0;
    const paidVal = parseFloat(itemData.paidAmount) || 0;

    if (qtyVal > 0 && costVal > 0) {
      const total = (qtyVal * costVal).toFixed(2);
      const pending = (qtyVal * costVal - paidVal).toFixed(2);
      const base = costVal.toFixed(2);

      setItemData((prev) => {
        if (
          prev.totalAmount === total &&
          prev.baseunitprice === base &&
          prev.pendingAmount === pending
        ) {
          return prev;
        }
        return {
          ...prev,
          totalAmount: total,
          baseunitprice: base,
          pendingAmount: pending,
        };
      });
    } else {
      const base = costVal > 0 ? costVal.toFixed(2) : '';
      const pendingVal = (parseFloat(itemData.totalAmount) || 0) - paidVal;
      const pending = Number.isNaN(pendingVal) ? '' : pendingVal.toFixed(2);
      setItemData((prev) => {
        if (
          prev.baseunitprice === base &&
          prev.pendingAmount === pending
        ) {
          return prev;
        }
        return {
          ...prev,
          baseunitprice: base,
          pendingAmount: pending,
        };
      });
    }
  }, [itemData.qty, itemData.paidAmount, selectedItem]);

  useEffect(() => {
    fetchSuppliers();
    fetchItems();

    // Set default GRN date to today if not updating
    if (!updateID) {
      const today = new Date().toISOString().split('T')[0];
      setItemData((prev) => ({ ...prev, grndate: today }));
    }
  }, []);

  useEffect(() => {
    if (updateID) {
      fetchGRNDetails(updateID);
    }
  }, [updateID]);

  const fetchSuppliers = async () => {
    try {
      const res = await axios.get(
        'https://serverdemo.ccsoftwares.com/Suppliers/getallactivesuppliers'
      );
      setSupplierList(res.data);
    } catch (error) {
      console.error('Error fetching suppliers:', error);
      toast.error('Failed to fetch suppliers.');
    }
  };

  const fetchItems = async () => {
    try {
      const res = await axios.get(
        'https://serverdemo.ccsoftwares.com/Item/getItemDetails'
      );
      const filteredItems = res.data.filter(
        (item) => parseInt(item.ItemCategoryID, 10) !== 5022
      );
      setItemList(filteredItems);
    } catch (error) {
      console.error('Error fetching items:', error);
      toast.error('Failed to fetch items.');
    }
  };

  const fetchGRNDetails = async (id) => {
    try {
      const data = {
        grnKey: id,
      };
      // Fetch GRN details here if needed
    } catch (error) {
      console.error('Error fetching GRN details:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === 'qty') {
      if (value === '') {
        setItemData((prev) => ({ ...prev, qty: '' }));
        return;
      }

      const qtyValue = Number(value);
      if (!Number.isFinite(qtyValue)) {
        setItemData((prev) => ({ ...prev, qty: '' }));
        return;
      }

      if (qtyValue <= 0) {
        setItemData((prev) => ({ ...prev, qty: '1' }));
        return;
      }

      setItemData((prev) => ({ ...prev, qty: value }));
      return;
    }

    setItemData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleClose = () => {
    setDialogOpen(false);
    clearForm();
  };

  const clearForm = () => {
    const today = new Date().toISOString().split('T')[0];
    setItemData({
      supplierName: '',
      grndate: today,
      totalAmount: '',
      paidAmount: '',
      pendingAmount: '',
      itemName: '',
      qty: '',
      baseunitprice: '',
      paymentMethod: 'Cash',
      productExpiry: 'No',
      expirationDate: '',
    });
    setSelectedItem(null); // Clear selected item
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Basic validation
    if (
      !itemData.supplierName ||
      !itemData.grndate ||
      !selectedItem ||
      !itemData.qty ||
      !itemData.totalAmount
    ) {
      toast.error('Please fill in all required fields.');
      return;
    }

    const qtyValue = Number(itemData.qty);
    if (!Number.isFinite(qtyValue) || qtyValue <= 0) {
      toast.error('Quantity must be greater than 0.');
      return;
    }

    // Prepare data to send
    const dataToSend = {
      itemName: selectedItem.ItemName,
      itemId: selectedItem.ItemID,
      supplierName: itemData.supplierName,
      qty: parseFloat(itemData.qty),
      totalAmount: parseFloat(itemData.totalAmount),
      paidAmount: parseFloat(itemData.paidAmount) || 0,
      pendingAmount: parseFloat(itemData.pendingAmount) || 0,
      baseunitprice: parseFloat(itemData.baseunitprice) || 0,
      expirationDate:
        itemData.productExpiry === 'Yes'
          ? itemData.expirationDate
          : '2100-12-31',
      grndate: itemData.grndate,
      paymentMethod: itemData.paymentMethod,
    };

    const stockData = {
      itemId: selectedItem.ItemID,
      itemName: selectedItem.ItemName,
      itemCategoryId: selectedItem.ItemCategoryID,
      stockIndate: itemData.grndate,
      stockExpireDate:
        itemData.productExpiry === 'Yes'
          ? itemData.expirationDate
          : '2100-12-31',
      stock: parseFloat(itemData.qty),
    };

    console.log(stockData)
    //console.log(dataToSend)

    if (updateID) {
      // Update existing GRN
      axios
        .post(
          'https://serverdemo.ccsoftwares.com/grn/updategrn',
          dataToSend
        )
        .then((res) => {
          toast.success('GRN successfully updated!');
          handleClose();
        })
        .catch((err) => {
          console.error('Error updating GRN:', err);
          toast.error('Failed to update GRN.');
        });
    } else {
      // Create new GRN
      setLoading(true);
      axios
        .post(
          'https://serverdemo.ccsoftwares.com/grn/CreateGrn',
          dataToSend
        )
        .then((res) => {
          axios
            .post(
              'https://serverdemo.ccsoftwares.com/stock/CreateShop1Stock',
              stockData
            )
            .then((res) => {
              setLoading(false);
              toast.success('GRN successfully created!');
              handleClose();
            })
            .catch((e) => {
              toast.error('Failed to update stock.');
              setLoading(false);
            });
        })
        .catch((err) => {
          console.error('Error submitting form:', err);
          toast.error('Failed to create GRN.');
          setLoading(false);
        });
    }
  };

  return (
    <Dialog
      open={dialogOpen}
      fullWidth
      maxWidth="md"
      onClose={handleClose}
    >
      <DialogTitle>
        {updateID ? 'UPDATE GRN' : 'CREATE GRN'}
        <IconButton
          onClick={handleClose}
          style={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: '#000',
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Container>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              {/* Supplier Dropdown */}
              <Grid item xs={12} md={6}>
                <TextField
                  select
                  fullWidth
                  name="supplierName"
                  label="Supplier "
                  value={itemData.supplierName}
                  onChange={handleChange}
                  required
                >
                  <MenuItem value="">Select Supplier</MenuItem>
                  {supplierList.map((supplier) => (
                    <MenuItem
                      key={supplier.SupplierID}
                      value={supplier.SupplierName}
                    >
                      {supplier.SupplierName}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              {/* GRN Date */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  name="grndate"
                  label="GRN Date *"
                  type="date"
                  value={itemData.grndate}
                  onChange={handleChange}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  required
                />
              </Grid>
              {/* Item Dropdown (Searchable) */}
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <Autocomplete
                    options={itemList}
                    getOptionLabel={(option) =>
                      option?.ItemName
                        ? `${option.ItemName}${option.ItemID ? ` (${option.ItemID})` : ''}`
                        : ''
                    }
                    isOptionEqualToValue={(option, value) =>
                      option?.ItemID != null &&
                      value?.ItemID != null &&
                      String(option.ItemID) === String(value.ItemID)
                    }
                    onChange={(event, newValue) => {
                      setSelectedItem(newValue);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Item "
                        required
                      />
                    )}
                    filterOptions={(options, { inputValue }) => {
                      const q = normalizeSearch(inputValue);
                      if (!q) return options;

                      return options.filter((option) => {
                        const name = normalizeSearch(option?.ItemName);
                        const id = normalizeSearch(option?.ItemID);
                        const barcode = normalizeSearch(option?.defbarcode);
                        return (
                          name.includes(q) ||
                          id.includes(q) ||
                          barcode.includes(q)
                        );
                      });
                    }}
                    renderOption={(props, option) => (
                      <li {...props}>
                        <span style={{ fontWeight: 'bold' }}>
                          {option.ItemName}
                        </span>
                        {option?.ItemID != null ? (
                          <span style={{ marginLeft: 8, color: '#000' }}>
                            ({option.ItemID})
                          </span>
                        ) : null}
                        {option?.defbarcode ? (
                          <span style={{ marginLeft: 8, color: '#000' }}>
                            | {option.defbarcode}
                          </span>
                        ) : null}
                      </li>
                    )}
                    value={selectedItem}
                  />
                </FormControl>
              </Grid>
              {/* Quantity */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  name="qty"
                  label="Quantity *"
                  type="number"
                  value={itemData.qty}
                  onChange={handleChange}
                  inputProps={{ min: 0.01, step: 0.01 }}
                  required
                />
              </Grid>
              {/* Total Amount */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  name="totalAmount"
                  label="Total Amount (auto from cost x qty)"
                  type="number"
                  value={itemData.totalAmount}
                  disabled
                />
              </Grid>
              {/* Base Unit Price (Auto-calculated) */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  name="baseunitprice"
                  label="Cost Price"
                  type="number"
                  value={itemData.baseunitprice}
                  disabled
                />
              </Grid>
              {/* Paid Amount */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  name="paidAmount"
                  label="Paid Amount"
                  type="number"
                  value={itemData.paidAmount}
                  onChange={handleChange}
                />
              </Grid>
              {/* Pending Amount (Auto-calculated) */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  name="pendingAmount"
                  label="Pending Amount"
                  type="number"
                  value={itemData.pendingAmount}
                  disabled
                />
              </Grid>
              {/* Payment Method */}
              <Grid item xs={12} md={6}>
                <TextField
                  select
                  fullWidth
                  name="paymentMethod"
                  label="Payment Method "
                  value={itemData.paymentMethod}
                  onChange={handleChange}
                  required
                >
                  <MenuItem value="Cheque">Cheque</MenuItem>
                  <MenuItem value="Online">Online transfer</MenuItem>
                  <MenuItem value="Cash">Cash</MenuItem>
                </TextField>
              </Grid>
              {/* Submit Button */}
              <Grid
                item
                xs={12}
                sx={{ display: 'flex', justifyContent: 'center' }}
              >
                <Button variant="outlined" type="submit" fullWidth>
                  {loading ? (
                    <>
                      <BeatLoader color="#000000" />
                    </>
                  ) : (
                    <>{updateID ? 'Update' : 'Submit'}</>
                  )}
                </Button>
              </Grid>
            </Grid>
          </form>
        </Container>
      </DialogContent>
    </Dialog>
  );
}
